import type {
  AdminUser,
  AdminUserDetail,
  Appearance,
  BlockedTerm,
  LeaderboardResponse,
  GhostBan,
  ModAction,
  OnlineGhost,
  OnlinePlayer,
  Page,
  PublicUser,
  Report,
  ReportCategory,
  ReportStatus,
  Role,
  Sanction,
  SanctionKind,
  ScoreResult,
} from "./types";

/** Erro de API com os detalhes por campo devolvidos pelo backend. */
export class ApiError extends Error {
  readonly status: number;
  readonly fields: Record<string, string>;
  readonly code: string;

  constructor(status: number, code: string, message: string, fields: Record<string, string> = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

interface ErrorBody {
  error?: string;
  message?: string;
  fields?: Record<string, string>;
}

async function request<T>(path: string, init: RequestInit = {}, token?: string): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);

  let response: Response;
  try {
    response = await fetch(path, { ...init, headers });
  } catch {
    throw new ApiError(0, "network", "Não foi possível falar com o servidor. Ele está rodando?");
  }

  if (response.status === 204) return undefined as T;

  const body = (await response.json().catch(() => ({}))) as T & ErrorBody;

  if (!response.ok) {
    const code = body.error ?? "erro";
    const message = body.message ?? (body.error || `Erro ${response.status}`);
    throw new ApiError(response.status, code, message, body.fields ?? {});
  }
  return body as T;
}

/** O cadastro ja devolve a sessao: confirmar o e-mail e opcional. */
export interface RegisterResult {
  token: string;
  expiresAt: string;
  user: PublicUser;
  emailSent: boolean;
  message: string;
}

export interface LoginResult {
  token: string;
  expiresAt: string;
  user: PublicUser;
}

export const api = {
  register(username: string, email: string, password: string, passwordConfirm: string, remember: boolean) {
    return request<RegisterResult>("/api/auth/register", {
      method: "POST",
      body: JSON.stringify({ username, email, password, passwordConfirm, remember }),
    });
  },

  login(login: string, password: string, remember: boolean) {
    return request<LoginResult>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ login, password, remember }),
    });
  },

  resendActivation(email: string) {
    return request<{ message: string }>("/api/auth/resend", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },

  forgotPassword(email: string) {
    return request<{ message: string }>("/api/auth/forgot", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },

  resetPassword(token: string, newPassword: string, newPasswordConfirm: string) {
    return request<{ message: string }>("/api/auth/reset", {
      method: "POST",
      body: JSON.stringify({ token, newPassword, newPasswordConfirm }),
    });
  },

  /** Reenvia a confirmacao de e-mail da propria conta (botao do perfil). */
  resendVerification(token: string) {
    return request<{ message: string }>("/api/me/verification", { method: "POST" }, token);
  },

  me(token: string) {
    return request<{ user: PublicUser; active: boolean }>("/api/me", {}, token);
  },

  leaderboard(slug: string, token?: string, limit = 10) {
    return request<LeaderboardResponse>(
      `/api/games/${encodeURIComponent(slug)}/leaderboard?limit=${limit}`,
      {},
      token,
    );
  },

  submitScore(slug: string, token: string, score: number, durationMs: number) {
    return request<ScoreResult>(
      `/api/games/${encodeURIComponent(slug)}/scores`,
      { method: "POST", body: JSON.stringify({ score, durationMs }) },
      token,
    );
  },

  updateDisplayName(token: string, displayName: string) {
    return request<{ user: PublicUser }>(
      "/api/me/name",
      { method: "PATCH", body: JSON.stringify({ displayName }) },
      token,
    );
  },

  updateAppearance(token: string, appearance: Appearance) {
    return request<{ user: PublicUser }>(
      "/api/me/appearance",
      { method: "PATCH", body: JSON.stringify(appearance) },
      token,
    );
  },

  /** Quem esta na cidade agora (qualquer jogador logado). */
  playersOnline(token: string) {
    return request<{ players: { id: string; name: string }[] }>("/api/players/online", {}, token);
  },

  report(token: string, targetId: string, category: ReportCategory, details: string) {
    return request<{ message: string }>(
      "/api/reports",
      { method: "POST", body: JSON.stringify({ targetId, category, details }) },
      token,
    );
  },

  updatePassword(token: string, currentPassword: string, newPassword: string) {
    return request<{ message: string }>(
      "/api/me/password",
      { method: "PUT", body: JSON.stringify({ currentPassword, newPassword }) },
      token,
    );
  },
};

type Message = { message: string };
const post = (body?: object): RequestInit => ({ method: "POST", body: JSON.stringify(body ?? {}) });
const userPath = (prefix: string, id: string, rest = "") => `/api/${prefix}/users/${encodeURIComponent(id)}${rest}`;

/** Rotas de moderacao: moderadores e admins (o servidor confere o cargo). */
export const modApi = {
  online(token: string) {
    return request<{ players: OnlinePlayer[]; ghosts: OnlineGhost[] }>("/api/mod/online", {}, token);
  },

  kickGhost(token: string, id: string, reason: string) {
    return request<Message>(`/api/mod/ghosts/${encodeURIComponent(id)}/kick`, post({ reason }), token);
  },

  /** Bane o IP do fantasma (so da entrada como fantasma) e derruba todos dele. */
  banGhost(token: string, id: string, reason: string) {
    return request<Message & { ban: GhostBan }>(`/api/mod/ghosts/${encodeURIComponent(id)}/ban`, post({ reason }), token);
  },

  ghostBans(token: string) {
    return request<{ bans: GhostBan[] }>("/api/mod/ghost-bans", {}, token);
  },

  revokeGhostBan(token: string, id: number) {
    return request<Message>(`/api/mod/ghost-bans/${id}`, { method: "DELETE" }, token);
  },

  /** minutes e ignorado no banimento (permanente). */
  sanction(token: string, id: string, kind: SanctionKind, minutes: number, reason: string) {
    return request<{ sanction: Sanction }>(userPath("mod", id, "/sanctions"), post({ kind, minutes, reason }), token);
  },

  revoke(token: string, id: string, kind: SanctionKind) {
    return request<{ revoked: number }>(userPath("mod", id, `/sanctions/${kind}`), { method: "DELETE" }, token);
  },

  kick(token: string, id: string, reason: string) {
    return request<Message>(userPath("mod", id, "/kick"), post({ reason }), token);
  },

  warn(token: string, id: string, message: string) {
    return request<Message>(userPath("mod", id, "/warn"), post({ message }), token);
  },

  resetName(token: string, id: string, reason: string) {
    return request<Message>(userPath("mod", id, "/reset-name"), post({ reason }), token);
  },

  resetAvatar(token: string, id: string, reason: string) {
    return request<Message>(userPath("mod", id, "/reset-avatar"), post({ reason }), token);
  },

  reports(token: string, status: ReportStatus, page = 1) {
    return request<Page & { reports: Report[]; open: number }>(`/api/mod/reports?status=${status}&page=${page}`, {}, token);
  },

  reportCount(token: string) {
    return request<{ open: number }>("/api/mod/reports/count", {}, token);
  },

  closeReport(token: string, id: number, status: "resolved" | "dismissed", note: string) {
    return request<{ report: Report }>(`/api/mod/reports/${id}/close`, post({ status, note }), token);
  },
};

export interface AccountChanges {
  username?: string;
  displayName?: string;
  email?: string;
  role?: Role;
}

/** Painel administrativo: so admins. */
export const adminApi = {
  users(token: string, query: string, role: Role | "", page: number) {
    const params = new URLSearchParams({ q: query, role, page: String(page) });
    return request<Page & { users: AdminUser[] }>(`/api/admin/users?${params}`, {}, token);
  },

  user(token: string, id: string) {
    return request<AdminUserDetail>(userPath("admin", id), {}, token);
  },

  update(token: string, id: string, changes: AccountChanges) {
    return request<{ user: AdminUser }>(userPath("admin", id), { method: "PATCH", body: JSON.stringify(changes) }, token);
  },

  setPassword(token: string, id: string, newPassword: string) {
    return request<Message>(userPath("admin", id, "/password"), { method: "PUT", body: JSON.stringify({ newPassword }) }, token);
  },

  endSessions(token: string, id: string) {
    return request<Message>(userPath("admin", id, "/logout"), post(), token);
  },

  /** game vazio apaga de todos os jogos. */
  deleteScores(token: string, id: string, game = "") {
    const query = game ? `?game=${encodeURIComponent(game)}` : "";
    return request<{ deleted: number }>(userPath("admin", id, `/scores${query}`), { method: "DELETE" }, token);
  },

  deleteUser(token: string, id: string, confirmUsername: string) {
    return request<Message>(
      userPath("admin", id),
      { method: "DELETE", body: JSON.stringify({ confirmUsername }) },
      token,
    );
  },

  announce(token: string, message: string) {
    return request<Message>("/api/admin/announce", post({ message }), token);
  },

  filterTerms(token: string) {
    return request<{ terms: BlockedTerm[] }>("/api/admin/filter", {}, token);
  },

  addTerm(token: string, term: string) {
    return request<Message>("/api/admin/filter", post({ term }), token);
  },

  removeTerm(token: string, term: string) {
    return request<Message>(`/api/admin/filter/${encodeURIComponent(term)}`, { method: "DELETE" }, token);
  },

  testFilter(token: string, text: string) {
    return request<{ chat: string; nameAllowed: boolean }>("/api/admin/filter/test", post({ text }), token);
  },

  actions(token: string, page: number) {
    return request<Page & { actions: ModAction[] }>(`/api/admin/actions?page=${page}`, {}, token);
  },
};
